import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import SectionSeparator from '../SectionSeparator/SectionSeparator';

export interface SectionProps {
  /** Content inside the section. */
  children: ReactNode;
  /** Optional header text. When set, header and separator are shown above the content. Separator always spans full width. */
  name?: string;
  /** Optional secondary text displayed on the right side of the name in the header. */
  subtitle?: string;
  /** Optional className for the root wrapper. */
  className?: string;
  /** Optional className for the content area only (below header/separator). Use e.g. p-4 or pt-4 pb-4 pl-4 pr-4 for padding. */
  contentClassName?: string;
}

export default function Section(props: SectionProps) {
  const { children, name = '', subtitle = '', className, contentClassName } = props;

  return (
    <div
      className={twMerge(
        'flex flex-col rounded-[16px] border border-pmw-separator-light dark:border-pmw-separator-dark bg-monoY dark:bg-pmw-eb-primary-dark',
        className,
      )}
    >
      {(name || subtitle) && (
        <div className="flex flex-col shrink-0 h-[50px]">
          <div className="flex-1 flex items-center justify-between gap-4 py-[13px] px-[8px]">
            {name && (
              <span className="min-w-[15%] truncate font-robotoMedium font-medium text-[14px] leading-[20px] text-monoX dark:text-monoY">
                {name}
              </span>
            )}
            {subtitle && (
              <span className="min-w-[15%] ml-auto text-right truncate font-robotoRegular font-normal text-[12px] leading-[16px] text-pmw-label-secondary-light dark:text-pmw-label-secondary-dark">
                {subtitle}
              </span>
            )}
          </div>
          <SectionSeparator variant="filled" />
        </div>
      )}
      <div className={twMerge('flex-1 p-2', contentClassName)}>{children}</div>
    </div>
  );
}
